
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from './StatusBadge';
import { DiscountActions } from './DiscountActions';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';

export function DiscountsList() {
  const { data: discounts, isLoading, error } = useQuery({
    queryKey: ['discounts'],
    queryFn: async () => {
      console.log('Fetching discount codes...');
      try {
        const { data, error } = await supabase
          .from('discount_codes')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Supabase error:', error);
          toast.error(error.message);
          throw error;
        }
        
        console.log('Fetched discount codes:', data?.length);
        return data;
      } catch (e) {
        console.error('Error in discount codes fetch:', e);
        throw e;
      }
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-50 rounded-md">
        Error loading discount codes: {(error as Error).message || 'Unknown error'}
      </div>
    );
  }

  if (!discounts || discounts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No discount codes found. Create your first discount code.
      </div>
    );
  }

  // Function to display discount value based on type
  const formatDiscountValue = (discount: any) => {
    if (discount.discount_type === 'percentage') {
      return `${discount.discount_percentage}%`;
    } else if (discount.discount_type === 'amount') {
      return `$${discount.discount_amount}`;
    }
    return 'N/A';
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Code</TableHead>
          <TableHead>Value</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Combinations</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {discounts.map((discount) => (
          <TableRow key={discount.id}>
            <TableCell>{discount.title}</TableCell>
            <TableCell className="font-mono uppercase font-semibold text-sm">{discount.code}</TableCell>
            <TableCell>{formatDiscountValue(discount)}</TableCell>
            <TableCell><StatusBadge status={discount.status} /></TableCell>
            <TableCell className="capitalize">{discount.discount_type}</TableCell>
            <TableCell>{discount.combinations ? 'Yes' : 'No'}</TableCell>
            <TableCell className="text-right">
              <DiscountActions discount={discount} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
